import React, { useState, useCallback, useEffect } from 'react'
import {
  Button,
  consoleTableMixin,
  CardsRow,
  CardsCol,
  Modal,
  ConsoleCard,
  message,
} from 'admin-web-ui'
import { PackageNameWhitelistingForm } from './PackageNameWhitelistingForm'
import { listSafetyNetPackageNames } from './list-safety-net-package-names'
import { deleteSafetyNetPackageName } from './delete-safety-net-package-name'
import { useAsyncFn } from 'react-use'
import { SafetyNetPackageName } from '../../../../api'
import { PackageNameWhitelistingList } from './PackageNameWhitelistingList'
import styled from 'styled-components'
import { useProjectContext } from '../../../../containers/Project'

type ModalState = 'open' | 'closed'

export const StyledPackageNameWhitelistingList = styled(
  PackageNameWhitelistingList,
)`
  ${consoleTableMixin}
  margin: -24px;
  max-width: inherit;
  & tr > td,
  tr > th {
    padding: 16px 24px;
  }
  ul.ant-pagination.ant-table-pagination {
    margin-right: 24px;
  }
`

export const PackageNameWhitelisting: React.FC = () => {
  const { projectClient } = useProjectContext()
  const [modalState, setModalState] = useState<ModalState>('closed')
  const handleOnCancel = useCallback(
    () => setModalState('closed'),
    [setModalState],
  )
  const [
    { loading: packageNamesLoading, value: packageNames },
    getPackageNames,
  ] = useAsyncFn(
    async (): Promise<SafetyNetPackageName[]> =>
      await listSafetyNetPackageNames(projectClient),
  )
  const handleOnOk = useCallback(() => {
    setModalState('closed')
    getPackageNames()
  }, [setModalState, getPackageNames])
  const deletePackageNameHandler = useCallback(
    async (packageName: string) => {
      try {
        await deleteSafetyNetPackageName(projectClient, packageName)
        getPackageNames()
        message.success(`Package name deleted: ${packageName}`)
      } catch {
        message.error('Package name failed to delete, please try again.')
      }
    },
    [projectClient, getPackageNames],
  )

  useEffect(() => void getPackageNames(), [getPackageNames])

  return (
    <CardsRow>
      <CardsCol span={24}>
        <ConsoleCard
          title="Android Package Name Whitelist"
          loading={packageNamesLoading}
          extra={
            <Button
              id="PackageNameWhitelisting__add-btn"
              type="primary"
              shape="round"
              onClick={() => setModalState('open')}
              disabled={false}
            >
              Add Package Name
            </Button>
          }
        >
          <StyledPackageNameWhitelistingList
            packageNames={packageNames || []}
            loading={packageNamesLoading}
            onDelete={deletePackageNameHandler}
          />
        </ConsoleCard>
      </CardsCol>
      <Modal
        open={modalState === 'open'}
        onCancel={handleOnCancel}
        onOk={handleOnOk}
        title="Add Android Package Name"
        footer={null}
      >
        <PackageNameWhitelistingForm
          onSuccess={handleOnOk}
          onCancel={handleOnCancel}
          projectClient={projectClient}
        />
      </Modal>
    </CardsRow>
  )
}
